import { useState, useEffect, useCallback } from "react";
import APIs from "../../../application/APIs";
import { useSnackbar } from "../../../context/Snackbar/SnackbarContext";
import { SnackbarType } from "../../../context/Snackbar/Types";

interface GmailOAuthState {
  connected: boolean;
  gmailAddress: string | null;
  authorizedAt: string | null;
  loading: boolean;
  testing: boolean;
}

export const useGmailOAuth = () => {
  const { showSnackbar } = useSnackbar();
  const [state, setState] = useState<GmailOAuthState>({
    connected: false,
    gmailAddress: null,
    authorizedAt: null,
    loading: true,
    testing: false,
  });

  const fetchStatus = useCallback(async () => {
    try {
      const response = await APIs.getGmailOAuthStatus();
      const data = response.data || response;
      setState((prev) => ({
        ...prev,
        connected: data.connected ?? false,
        gmailAddress: data.gmail_address || null,
        authorizedAt: data.authorized_at || null,
        loading: false,
      }));
    } catch {
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleConnect = useCallback(async () => {
    try {
      const response = await APIs.getGmailAuthorizationUrl();
      const authUrl = response.authorization_url || response.data?.authorization_url;
      if (!authUrl) {
        showSnackbar({
          title: "Error",
          message: "Failed to get authorization URL",
          type: SnackbarType.error,
        });
        return;
      }

      const popup = window.open(
        authUrl,
        "gmail-oauth",
        "width=600,height=700,left=200,top=100"
      );

      const pollInterval = setInterval(async () => {
        if (popup?.closed) {
          clearInterval(pollInterval);
          await fetchStatus();
          return;
        }
        try {
          const statusResponse = await APIs.getGmailOAuthStatus();
          const statusData = statusResponse.data || statusResponse;
          if (statusData.connected) {
            clearInterval(pollInterval);
            setState((prev) => ({
              ...prev,
              connected: true,
              gmailAddress: statusData.gmail_address || null,
              authorizedAt: statusData.authorized_at || null,
            }));
            showSnackbar({
              title: "Gmail Connected",
              message: "Your Gmail account has been connected successfully",
              type: SnackbarType.success,
            });
          }
        } catch {
          // Continue polling
        }
      }, 2000);
    } catch {
      showSnackbar({
        title: "Error",
        message: "Failed to start Gmail connection",
        type: SnackbarType.error,
      });
    }
  }, [fetchStatus, showSnackbar]);

  const handleDisconnect = useCallback(async () => {
    try {
      const response = await APIs.revokeGmailOAuth();
      if (response.is_success) {
        setState((prev) => ({
          ...prev,
          connected: false,
          gmailAddress: null,
          authorizedAt: null,
        }));
        showSnackbar({
          title: "Gmail Disconnected",
          message: "Your Gmail account has been disconnected",
          type: SnackbarType.info,
        });
      } else {
        showSnackbar({
          title: "Error",
          message: "Failed to disconnect Gmail",
          type: SnackbarType.error,
        });
      }
    } catch {
      showSnackbar({
        title: "Error",
        message: "Failed to disconnect Gmail",
        type: SnackbarType.error,
      });
    }
  }, [showSnackbar]);

  const handleTest = useCallback(async () => {
    setState((prev) => ({ ...prev, testing: true }));
    try {
      const response = await APIs.testGmailConnection();
      if (response.is_success) {
        showSnackbar({
          title: "Connection Test Passed",
          message: "Gmail connection is working correctly",
          type: SnackbarType.success,
        });
      } else {
        showSnackbar({
          title: "Connection Test Failed",
          message: response.error || "Gmail connection test failed",
          type: SnackbarType.error,
        });
      }
    } catch {
      showSnackbar({
        title: "Connection Test Failed",
        message: "Could not test Gmail connection",
        type: SnackbarType.error,
      });
    } finally {
      setState((prev) => ({ ...prev, testing: false }));
    }
  }, [showSnackbar]);

  return {
    ...state,
    handleConnect,
    handleDisconnect,
    handleTest,
  };
};
